import React, { MutableRefObject, useRef, memo } from "react";
import { CarouselIndicatorIcon } from "../icons/carousel-indicator";
import classes from "./button-indicators.module.css";

interface ButtonIndicatorsProps {
  totalSlideCount: number;
  currentSlideIndex: number;
  handleSlideIndexChange: (value: number) => void;
  slideRefs: MutableRefObject<(HTMLLIElement | null)[]>;
  children?: React.ReactNode;
}

const ButtonIndicators = memo(
  ({
    handleSlideIndexChange,
    currentSlideIndex,
    totalSlideCount,
    slideRefs,
  }: ButtonIndicatorsProps) => {
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const indicatorContainerWidth = 220;
    const indicatorWidth = indicatorContainerWidth / totalSlideCount;

    return (
      <div
        id="indicators-container"
        role="tablist"
        aria-label="Select a slide to display"
        className={classes.indicatorContainer}
      >
        {Array.from({ length: totalSlideCount }, (_, index) => (
          <button
            id={`indicator-${index}`}
            key={`indicator-${index}`}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            className={classes.buttonIndicator}
            aria-label={
              currentSlideIndex === index
                ? `Slide ${index + 1}`
                : `Go to slide ${index + 1}`
            }
            aria-selected={currentSlideIndex === index}
            aria-controls={`slide-${index}`}
            role="tab"
            tabIndex={0}
            type="button"
            onClick={() => {
              slideRefs.current[index]?.scrollIntoView({ behavior: "smooth" });
              handleSlideIndexChange(index);
            }}
          >
            <CarouselIndicatorIcon
              data-testid={`dot-${index}`}
              width={indicatorWidth}
              isActive={currentSlideIndex === index}
              fontSize={indicatorWidth}
              height={44}
              aria-hidden="true"
            />
            <span className={classes.visuallyHidden}>
              {index + 1}
              {currentSlideIndex === index && " (Current Item)"}
            </span>
          </button>
        ))}
      </div>
    );
  },
);

export default ButtonIndicators;
